export const BILLING_CURRENCY = 'usd';

export const PLAN_KEYS = {
  BASIC: 'basic',
  ALL_IN_ONE: 'all_in_one',
  ADDON_TRIPS: 'addon_trips',
  ADDON_ZONES: 'addon_zones',
  ADDON_REPORT: 'addon_report',
  FREE_TRIAL: 'free_trial',
};

const ADDON_PLAN_KEYS = new Set([
  PLAN_KEYS.ADDON_TRIPS,
  PLAN_KEYS.ADDON_ZONES,
  PLAN_KEYS.ADDON_REPORT,
]);

const BASIC_PLAN_NAMES = new Set(['Basic', 'Pilot Basic']);
const FULL_PLAN_NAMES = new Set(['All-in-One Bundle', 'Pilot full', 'Pilot Full']);
const ADDON_PLAN_NAMES = new Set(['Reports', 'Report', 'Trips', 'Zones']);

const resolvePlanName = (plan) => {
  if (typeof plan === 'string') {
    return plan;
  }
  return plan?.planName || '';
};

const resolvePlanKey = (plan) => plan?.planKey || null;

export const isBasicPlanName = (plan) => {
  if (resolvePlanKey(plan) === PLAN_KEYS.BASIC) {
    return true;
  }
  return BASIC_PLAN_NAMES.has(resolvePlanName(plan));
};

const normalizePlanNameKey = (name) => (
  String(name || '').trim().toLowerCase().replace(/\s+/g, ' ')
);

const isFullPlanNameKey = (nameKey) => (
  nameKey === 'all-in-one bundle'
  || nameKey === 'all in one bundle'
  || nameKey.includes('all-in-one')
  || nameKey.includes('all in one')
  || nameKey === 'pilot full'
);

export const isFullPlanName = (plan) => {
  if (resolvePlanKey(plan) === PLAN_KEYS.ALL_IN_ONE) {
    return true;
  }
  const planName = resolvePlanName(plan);
  if (FULL_PLAN_NAMES.has(planName)) {
    return true;
  }
  return isFullPlanNameKey(normalizePlanNameKey(planName));
};

export const isAddonPlanName = (plan) => {
  const planKey = resolvePlanKey(plan);
  if (planKey && ADDON_PLAN_KEYS.has(planKey)) {
    return true;
  }
  const planName = resolvePlanName(plan);
  if (ADDON_PLAN_NAMES.has(planName)) {
    return true;
  }
  const nameKey = planName.trim().toLowerCase();
  return ['reports', 'report', 'trips', 'trip', 'zones', 'zone'].includes(nameKey);
};

export const getPlanCatalogType = (plan) => {
  const planKey = resolvePlanKey(plan);
  if (planKey === PLAN_KEYS.ALL_IN_ONE || planKey === PLAN_KEYS.BASIC) {
    return 'subscription';
  }
  if (isFullPlanName(plan) || isBasicPlanName(plan)) {
    return 'subscription';
  }
  if (planKey === PLAN_KEYS.FREE_TRIAL || plan?.isTrial) {
    return 'trial';
  }
  if (planKey && ADDON_PLAN_KEYS.has(planKey)) {
    return 'addon';
  }
  if (isAddonPlanName(plan)) {
    return 'addon';
  }
  if (plan?.catalogType) {
    return plan.catalogType;
  }
  return 'other';
};

export const getPlanCatalogTypeLabel = (plan) => {
  const type = getPlanCatalogType(plan);
  if (type === 'addon') {
    return 'Add-on';
  }
  if (type === 'subscription') {
    return 'Subscription';
  }
  if (type === 'trial') {
    return 'Trial';
  }
  return 'Other';
};

export const descriptionToFormValue = (description) => {
  if (Array.isArray(description)) {
    return description.filter(Boolean).join('\n');
  }
  return description || '';
};

export const formatPlanDescription = (description, maxItems = 3) => {
  const lines = Array.isArray(description)
    ? description.filter(Boolean)
    : String(description || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  if (lines.length === 0) {
    return '-';
  }
  if (lines.length <= maxItems) {
    return lines.join(' · ');
  }
  return `${lines.slice(0, maxItems).join(' · ')} · +${lines.length - maxItems} more`;
};

export const formatCurrencyAmount = (amount) => {
  if (amount === undefined || amount === null || amount === '') {
    return '-';
  }
  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: BILLING_CURRENCY.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(numericAmount);
};

export const formatPlanPriceSummary = (plan) => {
  const monthly = plan?.monthlyPrice ?? plan?.price;
  const yearly = plan?.yearlyPrice;
  const monthlyLabel = formatCurrencyAmount(monthly);
  const yearlyLabel = yearly ? formatCurrencyAmount(yearly) : null;
  if (yearlyLabel) {
    return `${monthlyLabel} / mo · ${yearlyLabel} / yr`;
  }
  return `${monthlyLabel} / mo`;
};

export const getDisplayPlanName = (planName) => {
  if (planName === 'Pilot Basic') return 'Basic';
  if (planName === 'Pilot full') return 'Pilot Full';
  return planName;
};
